import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Plus, Search } from 'lucide-react';
import { aiRegistryClient, type AIModelConfig } from '../../services/ai-registry-client';

export type { AIModelConfig };

export interface AIModelConfigLocal {
  id: string;
  name: string;
  modelId: string;
  description: string;
  parameters: {
    temperature: number;
    topK?: number;
    topP?: number;
    maxOutputTokens?: number;
  };
  useCase: 'fast' | 'balanced' | 'creative' | 'thinking' | 'custom';
  metadata: {
    createdAt: string;
    updatedAt: string;
    createdBy: string;
  };
}

export function ConfigsMain() {
  const [configs, setConfigs] = useState<AIModelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [useCaseFilter, setUseCaseFilter] = useState<string>('');

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (useCaseFilter) filters.useCase = useCaseFilter as any;

      const data = await aiRegistryClient.getConfigs(filters);
      setConfigs(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configs');
      console.error('Error loading configs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, [useCaseFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadConfigs();
  };

  const filteredConfigs = configs.filter(config => {
    const matchesSearch =
      !searchTerm ||
      config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.modelId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getUseCaseColor = (useCase: string) => {
    switch (useCase) {
      case 'fast':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'balanced':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'creative':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'thinking':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200';
      case 'custom':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8 text-uw-interactive-primary" />
          <h1 className="text-3xl font-bold text-uw-text-primary">Model Configs</h1>
        </div>
        <Link
          to="/ai/configs/new"
          className="inline-flex items-center px-4 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Config
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-uw-text-tertiary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search configs..."
                className="w-full pl-10 pr-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Use Case
            </label>
            <select
              value={useCaseFilter}
              onChange={(e) => setUseCaseFilter(e.target.value)}
              className="w-full px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
            >
              <option value="">All Use Cases</option>
              <option value="fast">Fast</option>
              <option value="balanced">Balanced</option>
              <option value="creative">Creative</option>
              <option value="thinking">Thinking</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Config List */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uw-interactive-primary"></div>
          <p className="mt-4 text-uw-text-secondary">Loading configs...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {!loading && !error && filteredConfigs.length === 0 && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-12 text-center">
          <Settings className="w-16 h-16 text-uw-text-tertiary mx-auto mb-4" />
          <p className="text-uw-text-secondary">No configs found</p>
        </div>
      )}

      {!loading && !error && filteredConfigs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredConfigs.map((config) => (
            <Link
              key={config.id}
              to={`/ai/configs/${config.id}`}
              className="block bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6 hover:shadow-lg hover:border-uw-interactive-primary transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-uw-text-primary">
                  {config.name}
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded ${getUseCaseColor(config.useCase)}`}>
                  {config.useCase}
                </span>
              </div>
              <p className="text-sm text-uw-text-secondary mb-3">
                {config.description}
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-uw-text-tertiary">
                  <span>Model:</span>
                  <span className="text-uw-text-primary">{config.modelId}</span>
                </div>
                <div className="flex justify-between text-uw-text-tertiary">
                  <span>Temperature:</span>
                  <span className="text-uw-text-primary">{config.parameters.temperature}</span>
                </div>
                {config.parameters.maxOutputTokens && (
                  <div className="flex justify-between text-uw-text-tertiary">
                    <span>Max Tokens:</span>
                    <span className="text-uw-text-primary">
                      {config.parameters.maxOutputTokens.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
